import React, { useDeferredValue, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  Edit3,
  FileSpreadsheet,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { crmV2API } from '../services/api';

function clsx(...values) {
  return values.filter(Boolean).join(' ');
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function createImportState(selectedGroup) {
  return {
    open: false,
    step: 'config',
    targetType: selectedGroup ? 'existing' : 'new',
    groupId: selectedGroup?.id || '',
    newGroupName: '',
    file: null,
    preview: null,
    result: null,
    showDuplicateDetails: false,
    loading: false,
    error: '',
  };
}

function ModalShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-950/40" onClick={onClose} />
      <div className="absolute inset-0 overflow-y-auto p-4 md:p-8">
        <div className="flex min-h-full items-center justify-center">
          {children}
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  title,
  message,
  confirmLabel,
  deleting,
  onClose,
  onConfirm,
}) {
  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-700">
              <Trash2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
              <p className="mt-1 text-sm text-slate-500">{message}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close delete confirmation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            {deleting ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EditRecordModal({
  group,
  record,
  draftFields,
  saving,
  onClose,
  onChange,
  onSave,
}) {
  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Edit CRM v2 record</h2>
            <p className="text-sm text-slate-500">{group?.groupName || ''}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close edit record dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-4 px-6 py-5 md:grid-cols-2">
          {(group?.fieldNames || []).map((fieldName) => (
            <label key={fieldName} className="space-y-2">
              <span className="text-sm font-medium text-slate-700">{fieldName}</span>
              <textarea
                rows={3}
                value={draftFields[fieldName] ?? record?.fields?.[fieldName] ?? ''}
                onChange={(event) => onChange(fieldName, event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
              />
            </label>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Edit3 className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ImportModal({
  groups,
  importState,
  onClose,
  onConfigChange,
  onFileChange,
  onPreview,
  onCommit,
}) {
  const hasGroups = groups.length > 0;
  const previewGroupName = importState.preview?.fieldAlignment?.existingGroup?.groupName
    || importState.newGroupName
    || groups.find((group) => group.id === importState.groupId)?.groupName
    || '';
  const resultErrors = importState.result?.result?.errors || [];
  const duplicateErrors = resultErrors.filter((item) => item.type === 'duplicate');
  const nonDuplicateErrors = resultErrors.filter((item) => item.type !== 'duplicate');

  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-4xl rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Import data</h2>
            <p className="text-sm text-slate-500">Upload `.csv`, `.xls`, or `.xlsx`, review the parse, then import.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close import dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {importState.error && (
          <div className="mx-6 mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {importState.error}
          </div>
        )}

        {importState.step === 'config' && (
          <div className="space-y-6 px-6 py-6">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => onConfigChange('targetType', 'new')}
                className={clsx(
                  'rounded-3xl border px-5 py-5 text-left transition',
                  importState.targetType === 'new'
                    ? 'border-teal-500 bg-teal-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                )}
              >
                <div className="text-base font-semibold text-slate-900">New group</div>
                <p className="mt-2 text-sm text-slate-500">Create a new CRM v2 group from the imported file headers.</p>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (hasGroups) {
                    onConfigChange('targetType', 'existing');
                  }
                }}
                disabled={!hasGroups}
                className={clsx(
                  'rounded-3xl border px-5 py-5 text-left transition',
                  importState.targetType === 'existing'
                    ? 'border-teal-500 bg-teal-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300',
                  !hasGroups && 'cursor-not-allowed opacity-50 hover:border-slate-200'
                )}
              >
                <div className="text-base font-semibold text-slate-900">Existing group</div>
                <p className="mt-2 text-sm text-slate-500">
                  {hasGroups
                    ? 'Append rows to an existing group and expand fields if needed.'
                    : 'Disabled until at least one CRM v2 group exists.'}
                </p>
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 md:items-start">
              {importState.targetType === 'new' ? (
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">New group name</span>
                  <input
                    type="text"
                    value={importState.newGroupName}
                    onChange={(event) => onConfigChange('newGroupName', event.target.value)}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    placeholder="e.g. March Prospects"
                  />
                </label>
              ) : (
                <label className="space-y-2">
                  <span className="text-sm font-medium text-slate-700">Existing group</span>
                  <select
                    value={importState.groupId}
                    onChange={(event) => onConfigChange('groupId', event.target.value)}
                    disabled={!hasGroups}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    <option value="">Select a group</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.groupName}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="space-y-2">
                <span className="text-sm font-medium text-slate-700">Data file</span>
                <label className="flex h-full min-h-[108px] cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center transition hover:border-teal-400 hover:bg-teal-50">
                  <Upload className="mb-3 h-6 w-6 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">
                    {importState.file ? importState.file.name : 'Choose .csv, .xls, or .xlsx file'}
                  </span>
                  <span className="mt-1 text-xs text-slate-500">First row becomes field names. Remaining rows become records.</span>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    className="hidden"
                    onChange={(event) => onFileChange(event.target.files?.[0] || null)}
                  />
                </label>
              </label>
            </div>

            <div className="mt-4 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onPreview}
                disabled={importState.loading}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {importState.loading ? 'Parsing…' : 'Preview import'}
              </button>
            </div>
          </div>
        )}

        {importState.step === 'preview' && importState.preview && (
          <div className="space-y-6 px-6 py-6">
            <section className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Target group</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{previewGroupName || 'New group'}</div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-base font-semibold text-slate-900">Detected fields</h3>
                <div className="mt-4 max-h-56 overflow-auto rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap gap-2">
                    {importState.preview.parsedFieldNames.map((fieldName) => (
                      <span
                        key={fieldName}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700"
                      >
                        {fieldName}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{importState.preview.totalParsedRecords}</span> record(s) parsed from{' '}
                  <span className="font-semibold text-slate-900">{importState.preview.fileName}</span>
                </div>
                {importState.preview.previewRecords.map((record) => (
                  <div key={record.rowNumber} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Row {record.rowNumber}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      {Object.entries(record.fields).map(([fieldName, value]) => (
                        <div key={fieldName} className="rounded-2xl bg-slate-50 px-3 py-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{fieldName}</div>
                          <div className="mt-1 text-sm text-slate-800">{value || '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {importState.preview.previewRecords.length === 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    The sheet has headers but no non-empty data rows.
                  </div>
                )}
              </div>
            </section>

            <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Import warnings</h3>
                <p className="mt-1 text-sm text-slate-500">Review this before committing the import.</p>
              </div>

              {importState.preview.fieldAlignment.existingGroup && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900">
                    Existing group: {importState.preview.fieldAlignment.existingGroup.groupName}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {importState.preview.fieldAlignment.existingGroup.fieldNames.map((fieldName) => (
                      <span
                        key={fieldName}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700"
                      >
                        {fieldName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {importState.preview.fieldAlignment.newFieldsToAdd.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">This import will expand the group schema</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {importState.preview.fieldAlignment.newFieldsToAdd.map((fieldName) => (
                          <span key={fieldName} className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900">
                            {fieldName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {importState.preview.fieldAlignment.missingGroupFields.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">Fields already on the group but not present in this file</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {importState.preview.fieldAlignment.missingGroupFields.map((fieldName) => (
                      <span key={fieldName} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                        {fieldName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {importState.preview.warnings.length === 0 && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    No parse warnings detected.
                  </div>
                )}
                {importState.preview.warnings.map((warning) => (
                  <div key={warning} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    {warning}
                  </div>
                ))}
              </div>
            </section>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onConfigChange('step', 'config')}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={onCommit}
                  disabled={importState.loading}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Upload className="h-4 w-4" />
                  {importState.loading ? 'Importing…' : 'Confirm import'}
                </button>
              </div>
            </div>
          </div>
        )}

        {importState.step === 'result' && importState.result && (
          <div className="space-y-6 px-6 py-6">
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-700" />
                <div>
                  <h3 className="text-lg font-semibold text-emerald-900">Import complete</h3>
                  <p className="mt-1 text-sm text-emerald-900/80">{importState.result.group.groupName}</p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="text-sm text-slate-500">Imported</div>
                <div className="mt-2 text-3xl font-semibold text-slate-900">{importState.result.result.importedCount}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (importState.result.result.duplicateCount > 0) {
                    onConfigChange('showDuplicateDetails', !importState.showDuplicateDetails);
                  }
                }}
                className={clsx(
                  'rounded-3xl border p-5 text-left transition',
                  importState.result.result.duplicateCount > 0
                    ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                    : 'border-amber-200 bg-amber-50'
                )}
              >
                <div className="text-sm text-amber-800">
                  Duplicates
                  {importState.result.result.duplicateCount > 0 && (
                    <span className="ml-2 text-xs font-semibold uppercase tracking-[0.14em]">
                      {importState.showDuplicateDetails ? 'Hide rows' : 'Show rows'}
                    </span>
                  )}
                </div>
                <div className="mt-2 text-3xl font-semibold text-amber-900">{importState.result.result.duplicateCount}</div>
              </button>
              <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5">
                <div className="text-sm text-rose-800">Errors</div>
                <div className="mt-2 text-3xl font-semibold text-rose-900">{importState.result.result.errorCount}</div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">Processed rows</div>
              <div className="mt-2 text-sm text-slate-600">{importState.result.result.totalProcessed} row(s) were processed.</div>
            </div>

            {nonDuplicateErrors.length > 0 && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-4 text-base font-semibold text-slate-900">Import details</div>
                <div className="space-y-3">
                  {nonDuplicateErrors.map((errorItem, index) => (
                    <div
                      key={`${errorItem.type}-${errorItem.rowNumber}-${index}`}
                      className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
                    >
                      <span className="font-semibold">
                        {errorItem.rowNumber ? `Row ${errorItem.rowNumber}` : 'Record'}:
                      </span>{' '}
                      {errorItem.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importState.showDuplicateDetails && duplicateErrors.length > 0 && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-4 text-base font-semibold text-slate-900">Duplicate rows</div>
                <div className="space-y-3">
                  {duplicateErrors.map((errorItem, index) => (
                    <div
                      key={`${errorItem.type}-${errorItem.rowNumber}-${index}`}
                      className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                    >
                      <span className="font-semibold">
                        {errorItem.rowNumber ? `Row ${errorItem.rowNumber}` : 'Record'}:
                      </span>{' '}
                      {errorItem.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

export default function CrmV2Page({ embedded = false, groupsOnly = false, onSelectGroup = null }) {
  const [groupQuery, setGroupQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState('');
  const [groupsVersion, setGroupsVersion] = useState(0);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [recordQuery, setRecordQuery] = useState('');
  const [fieldFilters, setFieldFilters] = useState({});
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState('');
  const [recordsVersion, setRecordsVersion] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);

  const [editRecord, setEditRecord] = useState(null);
  const [editDraftFields, setEditDraftFields] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteRecordState, setDeleteRecordState] = useState({ open: false, record: null, deleting: false });
  const [deleteGroupState, setDeleteGroupState] = useState({ open: false, group: null, deleting: false });
  const [bulkDeleteState, setBulkDeleteState] = useState({ open: false, deleting: false });

  const [importState, setImportState] = useState(createImportState(null));

  const deferredGroupQuery = useDeferredValue(groupQuery);
  const deferredRecordQuery = useDeferredValue(recordQuery);
  const serializedFieldFilters = JSON.stringify(fieldFilters);
  const selectedGroupId = selectedGroup?.id || '';

  useEffect(() => {
    if (groups.length === 0) {
      setImportState((currentState) => (
        currentState.targetType === 'existing'
          ? {
              ...currentState,
              targetType: 'new',
              groupId: '',
              error: '',
            }
          : currentState
      ));
    }
  }, [groups.length]);

  useEffect(() => {
    let cancelled = false;

    async function loadGroups() {
      setGroupsLoading(true);
      setGroupsError('');

      try {
        const response = await crmV2API.listGroups({ q: deferredGroupQuery || undefined });
        if (cancelled) return;

        const nextGroups = Array.isArray(response.data?.groups) ? response.data.groups : [];
        setGroups(nextGroups);
        setSelectedGroup((currentGroup) => {
          if (!currentGroup) return currentGroup;
          return nextGroups.find((group) => group.id === currentGroup.id) || currentGroup;
        });
      } catch (error) {
        if (cancelled) return;
        setGroups([]);
        setGroupsError(error.response?.data?.message || error.message || 'Failed to load CRM v2 groups');
      } finally {
        if (!cancelled) {
          setGroupsLoading(false);
        }
      }
    }

    loadGroups();

    return () => {
      cancelled = true;
    };
  }, [deferredGroupQuery, groupsVersion]);

  useEffect(() => {
    if (!selectedGroupId) {
      setRecords([]);
      setTotal(0);
      setHasMore(false);
      setRecordsError('');
      return undefined;
    }

    let cancelled = false;

    async function loadRecords() {
      setRecordsLoading(true);
      setRecordsError('');

      try {
        const response = await crmV2API.listRecords(selectedGroupId, {
          page,
          limit,
          q: deferredRecordQuery || undefined,
          fieldFilters: serializedFieldFilters,
          sortField: sortField || undefined,
          sortDirection,
        });

        if (cancelled) return;

        if (response.data?.group) {
          setSelectedGroup(response.data.group);
        }
        setRecords(Array.isArray(response.data?.records) ? response.data.records : []);
        setTotal(Number(response.data?.total) || 0);
        setHasMore(Boolean(response.data?.hasMore));
      } catch (error) {
        if (cancelled) return;
        setRecords([]);
        setTotal(0);
        setHasMore(false);
        setRecordsError(error.response?.data?.message || error.message || 'Failed to load CRM v2 records');
      } finally {
        if (!cancelled) {
          setRecordsLoading(false);
        }
      }
    }

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, [selectedGroupId, page, limit, deferredRecordQuery, serializedFieldFilters, recordsVersion, sortField, sortDirection]);

  useEffect(() => {
    setSelectedRecordIds((currentIds) => currentIds.filter((recordId) => records.some((record) => record.id === recordId)));
  }, [records]);

  function openGroup(group) {
    setSelectedGroup(group);
    setRecordQuery('');
    setFieldFilters({});
    setPage(1);
    setTotal(0);
    setHasMore(false);
    setSortField('');
    setSortDirection('asc');
    setSelectedRecordIds([]);
  }

  function closeGroup() {
    setSelectedGroup(null);
    setRecordQuery('');
    setFieldFilters({});
    setRecords([]);
    setRecordsError('');
    setPage(1);
    setSortField('');
    setSortDirection('asc');
    setSelectedRecordIds([]);
  }

  function openEditDialog(record) {
    setEditRecord(record);
    setEditDraftFields({ ...(record?.fields || {}) });
  }

  function closeEditDialog() {
    setEditRecord(null);
    setEditDraftFields({});
    setEditSaving(false);
  }

  async function handleSaveRecord() {
    if (!editRecord) return;

    setEditSaving(true);
    try {
      const response = await crmV2API.updateRecord(editRecord.id, {
        fields: editDraftFields,
      });

      const nextRecord = response.data?.record;
      setRecords((currentRecords) => currentRecords.map((record) => (
        record.id === nextRecord.id ? nextRecord : record
      )));
      closeEditDialog();
      setRecordsVersion((current) => current + 1);
    } catch (error) {
      window.alert(error.response?.data?.message || error.message || 'Failed to update record');
      setEditSaving(false);
    }
  }

  function openDeleteRecordDialog(record) {
    setDeleteRecordState({ open: true, record, deleting: false });
  }

  function closeDeleteRecordDialog() {
    setDeleteRecordState({ open: false, record: null, deleting: false });
  }

  async function handleDeleteRecord() {
    if (!deleteRecordState.record?.id) return;

    setDeleteRecordState((currentState) => ({ ...currentState, deleting: true }));
    try {
      await crmV2API.deleteRecord(deleteRecordState.record.id);
      closeDeleteRecordDialog();
      setRecordsVersion((current) => current + 1);
      setGroupsVersion((current) => current + 1);
    } catch (error) {
      window.alert(error.response?.data?.message || error.message || 'Failed to delete record');
      setDeleteRecordState((currentState) => ({ ...currentState, deleting: false }));
    }
  }

  function openDeleteGroupDialog(group) {
    setDeleteGroupState({ open: true, group, deleting: false });
  }

  function closeDeleteGroupDialog() {
    setDeleteGroupState({ open: false, group: null, deleting: false });
  }

  async function handleDeleteGroup() {
    if (!deleteGroupState.group?.id) return;

    setDeleteGroupState((currentState) => ({ ...currentState, deleting: true }));
    try {
      await crmV2API.deleteGroup(deleteGroupState.group.id);
      if (selectedGroup?.id === deleteGroupState.group.id) {
        closeGroup();
      }
      closeDeleteGroupDialog();
      setGroupsVersion((current) => current + 1);
      setRecordsVersion((current) => current + 1);
    } catch (error) {
      window.alert(error.response?.data?.message || error.message || 'Failed to delete group');
      setDeleteGroupState((currentState) => ({ ...currentState, deleting: false }));
    }
  }

  function toggleSortForField(fieldName) {
    setPage(1);
    if (sortField === fieldName) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(fieldName);
    setSortDirection('asc');
  }

  function toggleRecordSelection(recordId) {
    setSelectedRecordIds((currentIds) => (
      currentIds.includes(recordId)
        ? currentIds.filter((id) => id !== recordId)
        : [...currentIds, recordId]
    ));
  }

  function toggleSelectAllVisibleRecords() {
    const visibleRecordIds = records.map((record) => record.id);
    const everyVisibleSelected = visibleRecordIds.length > 0 && visibleRecordIds.every((recordId) => selectedRecordIds.includes(recordId));

    setSelectedRecordIds((currentIds) => {
      if (everyVisibleSelected) {
        return currentIds.filter((recordId) => !visibleRecordIds.includes(recordId));
      }

      return Array.from(new Set([...currentIds, ...visibleRecordIds]));
    });
  }

  function openBulkDeleteDialog() {
    if (selectedRecordIds.length === 0) return;
    setBulkDeleteState({ open: true, deleting: false });
  }

  function closeBulkDeleteDialog() {
    setBulkDeleteState({ open: false, deleting: false });
  }

  async function handleBulkDeleteRecords() {
    if (selectedRecordIds.length === 0) return;

    setBulkDeleteState({ open: true, deleting: true });
    try {
      await crmV2API.bulkDeleteRecords(selectedRecordIds);
      setSelectedRecordIds([]);
      closeBulkDeleteDialog();
      setRecordsVersion((current) => current + 1);
      setGroupsVersion((current) => current + 1);
    } catch (error) {
      window.alert(error.response?.data?.message || error.message || 'Failed to delete selected records');
      setBulkDeleteState({ open: true, deleting: false });
    }
  }

  function openImportDialog() {
    setImportState({
      ...createImportState(selectedGroup),
      open: true,
    });
  }

  function closeImportDialog() {
    setImportState(createImportState(selectedGroup));
  }

  function handleImportDialogClose() {
    const importedGroup = importState.step === 'result' ? importState.result?.group || null : null;
    closeImportDialog();

    if (importedGroup && groupsOnly && onSelectGroup) {
      onSelectGroup(importedGroup);
    }
  }

  function updateImportState(key, value) {
    setImportState((currentState) => ({
      ...currentState,
      [key]: value,
      error: '',
    }));
  }

  async function handlePreviewImport() {
    if (!importState.file) {
      setImportState((currentState) => ({ ...currentState, error: 'Select a data file to import.' }));
      return;
    }

    if (importState.targetType === 'new' && !importState.newGroupName.trim()) {
      setImportState((currentState) => ({ ...currentState, error: 'Enter a group name for the new CRM v2 group.' }));
      return;
    }

    if (importState.targetType === 'existing' && !importState.groupId) {
      setImportState((currentState) => ({ ...currentState, error: 'Choose the existing group to import into.' }));
      return;
    }

    const formData = new FormData();
    formData.append('file', importState.file);
    formData.append('targetType', importState.targetType);
    if (importState.targetType === 'new') {
      formData.append('newGroupName', importState.newGroupName.trim());
    } else {
      formData.append('groupId', importState.groupId);
    }

    setImportState((currentState) => ({ ...currentState, loading: true, error: '' }));

    try {
      const response = await crmV2API.previewImport(formData);
      setImportState((currentState) => ({
        ...currentState,
        loading: false,
        step: 'preview',
        preview: response.data,
      }));
    } catch (error) {
      setImportState((currentState) => ({
        ...currentState,
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to preview import',
      }));
    }
  }

  async function handleCommitImport() {
    if (!importState.preview?.importSessionId) return;

    setImportState((currentState) => ({ ...currentState, loading: true, error: '' }));

    try {
      const response = await crmV2API.commitImport(importState.preview.importSessionId);
      const nextGroup = response.data?.group || null;

      setImportState((currentState) => ({
        ...currentState,
        loading: false,
        step: 'result',
        result: response.data,
        showDuplicateDetails: false,
      }));

      if (nextGroup && !groupsOnly) {
        setSelectedGroup(nextGroup);
      }
      setPage(1);
      setGroupsVersion((current) => current + 1);
      setRecordsVersion((current) => current + 1);
    } catch (error) {
      setImportState((currentState) => ({
        ...currentState,
        loading: false,
        error: error.response?.data?.message || error.message || 'Failed to import records',
      }));
    }
  }

  const showingGroupView = Boolean(selectedGroup?.id);
  const visibleRecordIds = records.map((record) => record.id);
  const hasVisibleRecords = visibleRecordIds.length > 0;
  const allVisibleSelected = hasVisibleRecords && visibleRecordIds.every((recordId) => selectedRecordIds.includes(recordId));
  const someVisibleSelected = hasVisibleRecords && visibleRecordIds.some((recordId) => selectedRecordIds.includes(recordId));

  return (
    <div className="min-h-full bg-[#f4f7f8] text-slate-900">
      <div className={clsx('mx-auto flex w-full max-w-[1600px] flex-col bg-[#f4f7f8] px-4 md:px-6', embedded ? 'gap-4 py-4' : 'gap-6 py-6')}>
        {!embedded && (
          <header className="rounded-[2rem] border border-slate-200 bg-white px-6 py-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                  <Database className="h-7 w-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-900">CRM v2</h1>
                  <p className="mt-1 text-sm text-slate-500">
                    Browse CRM groups, edit imported records, and import data files with preview and dedupe.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setGroupsVersion((current) => current + 1)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                >
                  <RefreshCw className={clsx('h-4 w-4', groupsLoading && 'animate-spin')} />
                  Refresh groups
                </button>
                <button
                  type="button"
                  onClick={openImportDialog}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  <Upload className="h-4 w-4" />
                  Import data
                </button>
              </div>
            </div>
          </header>
        )}

        {embedded && !showingGroupView && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setGroupsVersion((current) => current + 1)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              <RefreshCw className={clsx('h-4 w-4', groupsLoading && 'animate-spin')} />
              Refresh
            </button>
            <button
              type="button"
              onClick={openImportDialog}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Upload className="h-4 w-4" />
              Import data
            </button>
          </div>
        )}

        {!showingGroupView && (
          <>
            <section className="rounded-[2rem] border border-slate-200 bg-white px-5 py-4 shadow-sm min-w-0">
              <div className="flex w-full max-w-xl min-w-0 items-center gap-3 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 transition focus-within:border-teal-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-teal-200">
                <div className="pointer-events-none flex items-center">
                  <Search className="h-4 w-4 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={groupQuery}
                  onChange={(event) => setGroupQuery(event.target.value)}
                  className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                  placeholder="Search CRM v2 groups…"
                />
              </div>
            </section>

            {groupsError && (
              <section className="rounded-[2rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800 shadow-sm">
                {groupsError}
              </section>
            )}

            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {groupsLoading && Array.from({ length: 6 }).map((_, index) => (
                <div key={`group-skeleton-${index}`} className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="animate-pulse space-y-4">
                    <div className="h-5 w-40 rounded bg-slate-200" />
                    <div className="h-4 w-24 rounded bg-slate-200" />
                    <div className="grid grid-cols-2 gap-3">
                      <div className="h-14 rounded-2xl bg-slate-100" />
                      <div className="h-14 rounded-2xl bg-slate-100" />
                    </div>
                  </div>
                </div>
              ))}

              {!groupsLoading && groups.map((group) => (
                <div
                  key={group.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => (groupsOnly && onSelectGroup ? onSelectGroup(group) : openGroup(group))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      groupsOnly && onSelectGroup ? onSelectGroup(group) : openGroup(group);
                    }
                  }}
                  className="group rounded-[2rem] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-lg font-semibold text-slate-900">{group.groupName}</div>
                      <div className="mt-1 text-sm text-slate-500">Created {formatDate(group.createdAt)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        CRM v2
                      </div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openDeleteGroupDialog(group);
                        }}
                        className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Records</div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">{group.recordCount}</div>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Fields</div>
                      <div className="mt-2 text-2xl font-semibold text-slate-900">{group.fieldNames.length}</div>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {!groupsLoading && groups.length === 0 && (
              <section className="rounded-[2rem] border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm">
                <FileSpreadsheet className="mx-auto h-10 w-10 text-slate-400" />
                <h2 className="mt-4 text-xl font-semibold text-slate-900">No CRM v2 groups yet</h2>
                <p className="mt-2 text-sm text-slate-500">Import a data file to create the first CRM v2 group.</p>
                <button
                  type="button"
                  onClick={openImportDialog}
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  <Upload className="h-4 w-4" />
                  Import data
                </button>
              </section>
            )}
          </>
        )}

        {!groupsOnly && showingGroupView && (
          <>
            <section className="rounded-[2rem] border border-slate-200 bg-white px-5 py-5 shadow-sm">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={closeGroup}
                    className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                    aria-label="Back to CRM v2 groups"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">CRM v2 group</div>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-900">{selectedGroup.groupName}</h2>
                    <p className="mt-2 text-sm text-slate-500">
                      {selectedGroup.fieldNames.length} field(s) defined, {total} matching record(s) shown.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {selectedRecordIds.length > 0 && (
                    <button
                      type="button"
                      onClick={openBulkDeleteDialog}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete selected ({selectedRecordIds.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setRecordsVersion((current) => current + 1)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    <RefreshCw className={clsx('h-4 w-4', recordsLoading && 'animate-spin')} />
                    Refresh records
                  </button>
                  <button
                    type="button"
                    onClick={openImportDialog}
                    className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    <Upload className="h-4 w-4" />
                    Import into group
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.1fr),minmax(0,0.9fr)]">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 transition focus-within:border-teal-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-teal-200">
                  <div className="pointer-events-none flex items-center">
                    <Search className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    value={recordQuery}
                    onChange={(event) => {
                      setRecordQuery(event.target.value);
                      setPage(1);
                    }}
                    className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                    placeholder="Search across all group fields…"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {selectedGroup.fieldNames.map((fieldName) => (
                    <input
                      key={fieldName}
                      type="text"
                      value={fieldFilters[fieldName] || ''}
                      onChange={(event) => {
                        setFieldFilters((currentFilters) => ({
                          ...currentFilters,
                          [fieldName]: event.target.value,
                        }));
                        setPage(1);
                      }}
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-200"
                      placeholder={`Filter ${fieldName}`}
                    />
                  ))}
                </div>
              </div>
            </section>

            {recordsError && (
              <section className="rounded-[2rem] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800 shadow-sm">
                {recordsError}
              </section>
            )}

            <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left font-semibold">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(element) => {
                            if (element) {
                              element.indeterminate = someVisibleSelected && !allVisibleSelected;
                            }
                          }}
                          onChange={toggleSelectAllVisibleRecords}
                          aria-label="Select all visible records"
                          className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                        />
                      </th>
                      {selectedGroup.fieldNames.map((fieldName) => (
                        <th key={fieldName} className="whitespace-nowrap px-4 py-3 text-left font-semibold">
                          <button
                            type="button"
                            onClick={() => toggleSortForField(fieldName)}
                            className="inline-flex items-center gap-1.5 text-left transition hover:text-slate-900"
                          >
                            <span>{fieldName}</span>
                            {sortField === fieldName ? (
                              sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            ) : (
                              <span className="text-xs text-slate-400">↕</span>
                            )}
                          </button>
                        </th>
                      ))}
                      <th className="whitespace-nowrap px-4 py-3 text-left font-semibold">Updated</th>
                      <th className="whitespace-nowrap px-4 py-3 text-left font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {recordsLoading && Array.from({ length: 6 }).map((_, index) => (
                      <tr key={`record-skeleton-${index}`} className="animate-pulse">
                        <td className="px-4 py-4">
                          <div className="h-4 w-4 rounded bg-slate-200" />
                        </td>
                        {selectedGroup.fieldNames.map((fieldName) => (
                          <td key={`${fieldName}-${index}`} className="px-4 py-4">
                            <div className="h-4 w-32 rounded bg-slate-200" />
                          </td>
                        ))}
                        <td className="px-4 py-4">
                          <div className="h-4 w-28 rounded bg-slate-200" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="h-8 w-24 rounded bg-slate-200" />
                        </td>
                      </tr>
                    ))}

                    {!recordsLoading && records.length === 0 && (
                      <tr>
                        <td colSpan={selectedGroup.fieldNames.length + 3} className="px-6 py-14 text-center">
                          <div className="mx-auto max-w-md">
                            <Database className="mx-auto h-8 w-8 text-slate-400" />
                            <h3 className="mt-4 text-lg font-semibold text-slate-900">No records matched this view</h3>
                            <p className="mt-2 text-sm text-slate-500">
                              Adjust the search or field filters, or import more rows into this group.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}

                    {!recordsLoading && records.map((record) => (
                      <tr key={record.id} className="align-top hover:bg-slate-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedRecordIds.includes(record.id)}
                            onChange={() => toggleRecordSelection(record.id)}
                            aria-label={`Select record ${record.id}`}
                            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                          />
                        </td>
                        {selectedGroup.fieldNames.map((fieldName) => (
                          <td key={`${record.id}-${fieldName}`} className="max-w-[240px] px-4 py-4 text-slate-700">
                            <div className="whitespace-pre-wrap break-words">{record.fields[fieldName] || '—'}</div>
                          </td>
                        ))}
                        <td className="whitespace-nowrap px-4 py-4 text-slate-500">
                          {formatDate(record.updatedAt || record.createdAt)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditDialog(record)}
                              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                            >
                              <Edit3 className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => openDeleteRecordDialog(record)}
                              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-slate-600">
                  Showing <span className="font-semibold text-slate-900">{records.length}</span> of{' '}
                  <span className="font-semibold text-slate-900">{total}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={limit}
                    onChange={(event) => {
                      setLimit(Number(event.target.value));
                      setPage(1);
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                  >
                    {[10, 25, 50, 100].map((value) => (
                      <option key={value} value={value}>
                        {value} / page
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                    disabled={page === 1}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <div className="text-sm text-slate-600">
                    Page <span className="font-semibold text-slate-900">{page}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPage((currentPage) => (hasMore ? currentPage + 1 : currentPage))}
                    disabled={!hasMore}
                    className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      {editRecord && (
        <EditRecordModal
          group={selectedGroup}
          record={editRecord}
          draftFields={editDraftFields}
          saving={editSaving}
          onClose={closeEditDialog}
          onChange={(fieldName, value) => {
            setEditDraftFields((currentFields) => ({
              ...currentFields,
              [fieldName]: value,
            }));
          }}
          onSave={handleSaveRecord}
        />
      )}

      {deleteRecordState.open && (
        <ConfirmDeleteModal
          title="Delete CRM v2 record"
          message="This will permanently remove the selected record from the group."
          confirmLabel="Delete record"
          deleting={deleteRecordState.deleting}
          onClose={closeDeleteRecordDialog}
          onConfirm={handleDeleteRecord}
        />
      )}

      {deleteGroupState.open && (
        <ConfirmDeleteModal
          title="Delete CRM v2 group"
          message={`This will permanently delete "${deleteGroupState.group?.groupName || 'this group'}" and all of its records.`}
          confirmLabel="Delete group"
          deleting={deleteGroupState.deleting}
          onClose={closeDeleteGroupDialog}
          onConfirm={handleDeleteGroup}
        />
      )}

      {bulkDeleteState.open && (
        <ConfirmDeleteModal
          title="Delete selected CRM v2 records"
          message={`This will permanently delete ${selectedRecordIds.length} selected record(s) from the group.`}
          confirmLabel="Delete selected"
          deleting={bulkDeleteState.deleting}
          onClose={closeBulkDeleteDialog}
          onConfirm={handleBulkDeleteRecords}
        />
      )}

      {importState.open && (
        <ImportModal
          groups={groups}
          importState={importState}
          onClose={handleImportDialogClose}
          onConfigChange={updateImportState}
          onFileChange={(file) => updateImportState('file', file)}
          onPreview={handlePreviewImport}
          onCommit={handleCommitImport}
        />
      )}
    </div>
  );
}
